import React from 'react';
import { Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';

export interface TimeRangePreset {
  value: string;
  label: string;
}

interface TimeRangeSelectProps {
  presets: TimeRangePreset[];
  /** Active preset value; undefined means a custom range is active. */
  timeRange?: string;
  startTime?: string;
  endTime?: string;
  /** Called with a preset value, or 'custom' when the custom option is picked. */
  onSelectPreset: (value: string) => void;
  onStartChange: (iso: string) => void;
  onEndChange: (iso: string) => void;
  /** When provided, an Apply button is shown for the custom range. */
  onApply?: () => void;
  customLabel?: string;
}

const parseDateTime = (value: string | undefined): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/** Time-window picker shared by the event-management views: a preset dropdown
 *  plus an optional custom from/to range. Presets are caller-defined. */
const TimeRangeSelect: React.FC<TimeRangeSelectProps> = ({
  presets, timeRange, startTime, endTime,
  onSelectPreset, onStartChange, onEndChange, onApply, customLabel = 'Custom range',
}) => {
  const isCustom = !timeRange;

  return (
    <>
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Time</InputLabel>
        <Select
          value={isCustom ? 'custom' : timeRange}
          onChange={(e) => onSelectPreset(e.target.value)}
          label="Time"
        >
          {presets.map((p) => (
            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
          ))}
          <MenuItem value="custom">{customLabel}</MenuItem>
        </Select>
      </FormControl>

      {isCustom && (
        <>
          <DateTimePicker
            label="Start"
            value={parseDateTime(startTime)}
            onChange={(value: Dayjs | null) => onStartChange(value?.isValid() ? value.toISOString() : '')}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
          />
          <DateTimePicker
            label="End"
            value={parseDateTime(endTime)}
            onChange={(value: Dayjs | null) => onEndChange(value?.isValid() ? value.toISOString() : '')}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
          />
          {onApply && (
            <Button size="small" variant="outlined" disabled={!startTime || !endTime} onClick={onApply}>
              Apply
            </Button>
          )}
        </>
      )}
    </>
  );
};

export default TimeRangeSelect;
